import DocumentService from "../Services/documents.service.js";
import ExternalTransactionsService from "../Services/externalTransaction.service.js";
import Constants from "../assests/Constants/constants.js";

const FC_AMOUNT_DECIMALS = 2;

const toIso = (value) => (value ? new Date(value).toISOString() : null);

/**
 * Payload for a journal entry created while reconciling a bank account. It is locked, processed and
 * flagged as bank-match made, so removing the match later deletes it again.
 *
 * `amount` is the shekel amount. When `foreignCurrencyCode` is given the rate is stored as a manual
 * snapshot so the server keeps exactly the shekel amount the user saw.
 */
export const buildBankMatchJournalPayload = ({
  caseId,
  accountantId,
  sortCodeId,
  reference,
  description,
  amount,
  documentDate,
  reportingMonthDate,
  registrationMonthDate,
  creditAccount,
  debitAccount,
  vat,
  externalId = null,
  foreignCurrencyCode = null,
  foreignCurrencyAmount = null,
  exchangeRate = null,
}) => {
  const isForeign = foreignCurrencyCode != null && foreignCurrencyCode !== "";
  const reportingMonth = toIso(reportingMonthDate) ?? new Date().toISOString();
  return {
    caseId,
    accountantId,
    sortCodeId,
    reference,
    description,
    amount: parseFloat(Math.abs(Number(amount)).toFixed(2)),
    documentDate: toIso(documentDate),
    reportingMonthDate: reportingMonth,
    registrationMonthDate: toIso(registrationMonthDate) ?? reportingMonth,
    creditAccount,
    debitAccount,
    vat,
    isMadeFromBankMatch: true,
    addAsProcessed: true,
    externalId,
    foreignCurrencyCode: isForeign ? foreignCurrencyCode : null,
    foreignCurrencyAmount:
      isForeign && foreignCurrencyAmount != null && foreignCurrencyAmount !== ""
        ? parseFloat(Math.abs(Number(foreignCurrencyAmount)).toFixed(FC_AMOUNT_DECIMALS))
        : null,
    exchangeRateType: isForeign ? Constants.ExchangeRateType.Manual : null,
    exchangeRate: isForeign ? exchangeRate : null,
    isLocked: true,
  };
};

/**
 * Creates one bank match. `transactionDate` is the oldest bank date in the group (the date the match
 * is filed under); it falls back to today for book-only matches.
 */
export const createBankMatch = ({
  caseId,
  accountCodeId,
  externalIds,
  internalTransactions,
  transactionDate,
  token,
}) =>
  ExternalTransactionsService.CreateMatch(
    {
      CaseId: caseId,
      AccountCodeId: accountCodeId,
      TransactionDate: (transactionDate ? new Date(transactionDate) : new Date()).toISOString(),
      ExternalTransactionIds: externalIds.map((id) => ({ Id: id })),
      InternalTransactions: internalTransactions.map((row) => ({ Id: row.id, Type: row.type })),
    },
    token
  );

/**
 * Optionally creates the journal entry that balances a group, then matches the whole group (the new
 * entry included). Returns the new journal entry id, or throws with the server message.
 * The server deletes the temporary journal entry itself when the match is rejected.
 */
export const createJournalEntryAndMatch = async ({
  caseId,
  accountCodeId,
  externalIds,
  internalTransactions,
  transactionDate,
  journalPayload,
  token,
}) => {
  let journalEntryId = null;
  const internal = [...internalTransactions];

  if (journalPayload) {
    const response = await DocumentService.UploadDocumentPayload("JournalEntries", journalPayload, token);
    if (!response.ok) {
      throw new Error((await response.text()) || "Failed to create journal entry");
    }
    const data = await response.json();
    journalEntryId = data.journalEntryId;
    internal.push({ id: journalEntryId, type: "JournalEntries" });
  }

  const matchResponse = await createBankMatch({
    caseId,
    accountCodeId,
    externalIds,
    internalTransactions: internal,
    transactionDate,
    token,
  });
  if (!matchResponse.ok) {
    throw new Error((await matchResponse.text()) || "Failed to create match");
  }

  return journalEntryId;
};
